'use client';

/**
 * Tech Radar Builder
 * Track technology adoption decisions: Adopt, Trial, Assess, Hold
 */

import { useState, useEffect, useCallback } from 'react';
import ToolSEO from '@/components/ToolSEO';
import { RadarVisualization } from '@/components/tech-radar/RadarVisualization';
import { BlipEditor } from '@/components/tech-radar/BlipEditor';
import {
  TechRadar,
  RadarBlip,
  RadarQuadrant,
  RadarRing,
  RING_CONFIG,
  QUADRANT_CONFIG,
  generateRadarId,
} from '@/lib/tech-radar/types';
import {
  saveRadarToStorage,
  loadRadarFromStorage,
  listRadarsFromStorage,
  deleteRadarFromStorage,
} from '@/lib/tech-radar/storage';

// Tier access levels
const TIER_HIERARCHY = { free: 0, pro: 1, team: 2 };
type UserTier = 'free' | 'pro' | 'team';

function getFeatureAccess(tier: UserTier) {
  const tierLevel = TIER_HIERARCHY[tier];
  return {
    maxRadars: tier === 'free' ? 1 : tier === 'pro' ? 5 : Infinity,
    maxBlips: tier === 'free' ? 20 : tier === 'pro' ? 100 : Infinity,
    canExport: tierLevel >= TIER_HIERARCHY.free,
    canExportJSON: tierLevel >= TIER_HIERARCHY.pro,
  };
}

function createDefaultRadar(): TechRadar {
  const now = new Date().toISOString();
  return {
    id: generateRadarId(),
    name: 'My Tech Radar',
    description: '',
    blips: [],
    settings: {},
    created_at: now,
    updated_at: now,
  };
}

export default function TechRadarPage() {
  // State
  const [userTier] = useState<UserTier>('pro');
  const [radars, setRadars] = useState<TechRadar[]>([]);
  const [currentRadar, setCurrentRadar] = useState<TechRadar | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editor states
  const [selectedBlip, setSelectedBlip] = useState<RadarBlip | null>(null);
  const [isAddingBlip, setIsAddingBlip] = useState(false);
  const [filterQuadrant, setFilterQuadrant] = useState<RadarQuadrant | 'all'>('all');
  const [filterRing, setFilterRing] = useState<RadarRing | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const features = getFeatureAccess(userTier);

  // Load radars
  const loadRadars = useCallback(async () => {
    try {
      setLoading(true);
      const list = await listRadarsFromStorage();
      setRadars(list);
    } catch (err) {
      console.error('[TechRadar] Failed to load:', err);
      setError('Failed to load tech radars');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRadars();
  }, [loadRadars]);

  // Auto-save
  useEffect(() => {
    if (!currentRadar || !hasChanges) return;

    const timer = setTimeout(async () => {
      try {
        setSaving(true);
        const updated = { ...currentRadar, updated_at: new Date().toISOString() };
        await saveRadarToStorage(updated);
        setCurrentRadar(updated);
        setHasChanges(false);
        setRadars((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      } catch (err) {
        console.error('[TechRadar] Save failed:', err);
      } finally {
        setSaving(false);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [currentRadar, hasChanges]);

  // Handlers
  const handleCreateRadar = async () => {
    if (radars.length >= features.maxRadars) {
      setError(`Free tier limited to ${features.maxRadars} radar. Upgrade to Pro for more.`);
      return;
    }

    const newRadar = createDefaultRadar();
    await saveRadarToStorage(newRadar);
    setRadars((prev) => [newRadar, ...prev]);
    setCurrentRadar(newRadar);
  };

  const handleOpenRadar = async (id: string) => {
    try {
      const radar = await loadRadarFromStorage(id);
      if (radar) {
        setCurrentRadar(radar);
        setHasChanges(false);
      }
    } catch (err) {
      console.error('[TechRadar] Failed to open:', err);
      setError('Failed to open radar');
    }
  };

  const handleDeleteRadar = async (id: string) => {
    if (!confirm('Delete this tech radar?')) return;
    await deleteRadarFromStorage(id);
    setRadars((prev) => prev.filter((r) => r.id !== id));
    if (currentRadar?.id === id) {
      setCurrentRadar(null);
    }
  };

  const handleSaveBlip = (blip: RadarBlip) => {
    if (!currentRadar) return;

    if (currentRadar.blips.length >= features.maxBlips && !currentRadar.blips.find((b) => b.id === blip.id)) {
      setError(`Limited to ${features.maxBlips} technologies. Upgrade for more.`);
      return;
    }

    const existingIdx = currentRadar.blips.findIndex((b) => b.id === blip.id);
    let newBlips: RadarBlip[];
    if (existingIdx >= 0) {
      newBlips = [...currentRadar.blips];
      newBlips[existingIdx] = blip;
    } else {
      newBlips = [...currentRadar.blips, blip];
    }

    setCurrentRadar({ ...currentRadar, blips: newBlips });
    setHasChanges(true);
    setSelectedBlip(null);
    setIsAddingBlip(false);
  };

  const handleDeleteBlip = (id: string) => {
    if (!currentRadar) return;
    setCurrentRadar({
      ...currentRadar,
      blips: currentRadar.blips.filter((b) => b.id !== id),
    });
    setHasChanges(true);
    setSelectedBlip(null);
  };

  const handleExportMarkdown = () => {
    if (!currentRadar) return;

    let md = `# ${currentRadar.name}\n\n`;
    if (currentRadar.description) {
      md += `${currentRadar.description}\n\n`;
    }
    md += `*Generated ${new Date().toLocaleDateString()}*\n\n---\n\n`;

    const quadrants: RadarQuadrant[] = ['techniques', 'platforms', 'tools', 'languages_frameworks'];
    const rings: RadarRing[] = ['adopt', 'trial', 'assess', 'hold'];

    quadrants.forEach((quadrant) => {
      const config = QUADRANT_CONFIG[quadrant];
      const quadrantBlips = currentRadar.blips.filter((b) => b.quadrant === quadrant);
      if (quadrantBlips.length === 0) return;

      md += `## ${config.icon} ${config.label}\n\n`;

      rings.forEach((ring) => {
        const ringBlips = quadrantBlips.filter((b) => b.ring === ring);
        if (ringBlips.length === 0) return;

        const ringConfig = RING_CONFIG[ring];
        md += `### ${ringConfig.label}\n\n`;

        ringBlips.forEach((blip) => {
          md += `- **${blip.name}**`;
          if (blip.isNew) md += ' 🆕';
          if (blip.movedFrom) md += ` (moved from ${RING_CONFIG[blip.movedFrom].label})`;
          md += '\n';
          if (blip.description) md += `  - ${blip.description}\n`;
          if (blip.rationale) md += `  - *Rationale:* ${blip.rationale}\n`;
        });
        md += '\n';
      });
    });

    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentRadar.name.replace(/\s+/g, '-').toLowerCase()}-tech-radar.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportJSON = () => {
    if (!currentRadar) return;

    const json = JSON.stringify(currentRadar, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentRadar.name.replace(/\s+/g, '-').toLowerCase()}-tech-radar.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Filtered blips
  const filteredBlips = currentRadar?.blips.filter((blip) => {
    if (filterQuadrant !== 'all' && blip.quadrant !== filterQuadrant) return false;
    if (filterRing !== 'all' && blip.ring !== filterRing) return false;
    if (searchQuery && !blip.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  }) || [];

  // Radar list view
  if (!currentRadar) {
    return (
      <>
        <ToolSEO
          toolName="Tech Radar Builder"
          toolSlug="tech-radar"
          description="Track technology adoption decisions with a visual tech radar. Categorize technologies as Adopt, Trial, Assess, or Hold."
          category="planner"
          isPremium={true}
        />
        <main className="min-h-screen bg-background text-foreground py-12 px-6">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold">Tech Radar Builder</h1>
                <p className="text-foreground-secondary mt-2">
                  Track technology adoption decisions with a visual radar
                </p>
              </div>
              <button
                onClick={handleCreateRadar}
                className="px-4 py-2 bg-accent text-accent-foreground rounded font-medium hover:bg-accent-hover transition-colors"
              >
                + New Radar
              </button>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
                {error}
                <button onClick={() => setError(null)} className="ml-4 underline">Dismiss</button>
              </div>
            )}

            {/* Loading */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-foreground-tertiary">Loading radars...</p>
              </div>
            ) : radars.length === 0 ? (
              <div className="border border-dashed border-border rounded-lg p-12 text-center">
                <div className="text-4xl mb-4">📡</div>
                <h3 className="text-lg font-medium mb-2">No tech radars yet</h3>
                <p className="text-foreground-tertiary mb-6">
                  Create your first tech radar to track technology adoption decisions
                </p>
                <button
                  onClick={handleCreateRadar}
                  className="px-6 py-3 bg-accent text-accent-foreground rounded font-medium hover:bg-accent-hover transition-colors"
                >
                  Create Tech Radar
                </button>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {radars.map((radar) => (
                  <div
                    key={radar.id}
                    className="p-6 border border-border rounded-lg hover:border-accent/40 transition-colors cursor-pointer group"
                    onClick={() => handleOpenRadar(radar.id)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-foreground group-hover:text-accent transition-colors">
                          {radar.name}
                        </h3>
                        {radar.description && (
                          <p className="text-sm text-foreground-tertiary mt-1 line-clamp-2">{radar.description}</p>
                        )}
                      </div>
                      <span className="text-sm text-foreground-muted">{radar.blips.length} items</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-foreground-muted">
                      <span>Updated {new Date(radar.updated_at).toLocaleDateString()}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRadar(radar.id);
                        }}
                        className="text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Ring legend */}
            <div className="mt-12 p-6 bg-cream/5 border border-border-muted rounded-lg">
              <h3 className="font-semibold mb-4">Ring Definitions</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {(['adopt', 'trial', 'assess', 'hold'] as RadarRing[]).map((ring) => {
                  const config = RING_CONFIG[ring];
                  return (
                    <div key={ring} className="flex items-start gap-3">
                      <div className="w-4 h-4 rounded-full mt-1" style={{ backgroundColor: config.color }} />
                      <div>
                        <div className="font-medium text-foreground">{config.label}</div>
                        <div className="text-sm text-foreground-tertiary">{config.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </main>
      </>
    );
  }

  // Radar editor view
  return (
    <>
      <ToolSEO
        toolName="Tech Radar Builder"
        toolSlug="tech-radar"
        description="Track technology adoption decisions with a visual tech radar. Categorize technologies as Adopt, Trial, Assess, or Hold."
        category="planner"
        isPremium={true}
      />
      <main className="min-h-screen bg-background text-foreground py-8 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setCurrentRadar(null)}
                className="text-foreground-tertiary hover:text-foreground transition-colors"
              >
                ← Back
              </button>
              <div>
                <input
                  type="text"
                  value={currentRadar.name}
                  onChange={(e) => {
                    setCurrentRadar({ ...currentRadar, name: e.target.value });
                    setHasChanges(true);
                  }}
                  className="text-2xl font-bold bg-transparent border-none text-foreground focus:outline-none"
                />
                <div className="text-sm text-foreground-tertiary">
                  {currentRadar.blips.length} technologies
                  {saving && <span className="ml-2 text-accent">Saving...</span>}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleExportMarkdown}
                className="px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 transition-colors"
              >
                Export MD
              </button>
              {features.canExportJSON && (
                <button
                  onClick={handleExportJSON}
                  className="px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 transition-colors"
                >
                  Export JSON
                </button>
              )}
              <button
                onClick={() => setIsAddingBlip(true)}
                className="px-4 py-2 bg-accent text-accent-foreground rounded font-medium hover:bg-accent-hover transition-colors"
              >
                + Add Technology
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
              {error}
              <button onClick={() => setError(null)} className="ml-4 underline">Dismiss</button>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Radar visualization */}
            <div className="lg:col-span-2 flex justify-center">
              <RadarVisualization
                blips={currentRadar.blips}
                size={Math.min(600, typeof window !== 'undefined' ? window.innerWidth - 100 : 600)}
                onBlipClick={(blip) => setSelectedBlip(blip)}
                selectedBlipId={selectedBlip?.id}
              />
            </div>

            {/* Blip list */}
            <div className="lg:col-span-1">
              {/* Filters */}
              <div className="mb-4 space-y-3">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search technologies..."
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground placeholder-foreground-muted"
                />
                <div className="flex gap-2">
                  <select
                    value={filterQuadrant}
                    onChange={(e) => setFilterQuadrant(e.target.value as RadarQuadrant | 'all')}
                    className="flex-1 px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
                  >
                    <option value="all">All Quadrants</option>
                    {(Object.keys(QUADRANT_CONFIG) as RadarQuadrant[]).map((q) => (
                      <option key={q} value={q}>{QUADRANT_CONFIG[q].label}</option>
                    ))}
                  </select>
                  <select
                    value={filterRing}
                    onChange={(e) => setFilterRing(e.target.value as RadarRing | 'all')}
                    className="flex-1 px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
                  >
                    <option value="all">All Rings</option>
                    {(Object.keys(RING_CONFIG) as RadarRing[]).map((r) => (
                      <option key={r} value={r}>{RING_CONFIG[r].label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Blip list */}
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {filteredBlips.length === 0 ? (
                  <div className="text-center py-8 text-foreground-tertiary">
                    {currentRadar.blips.length === 0
                      ? 'No technologies added yet'
                      : 'No technologies match filters'}
                  </div>
                ) : (
                  filteredBlips.map((blip) => (
                    <div
                      key={blip.id}
                      onClick={() => setSelectedBlip(blip)}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedBlip?.id === blip.id
                          ? 'border-accent bg-accent-muted'
                          : 'border-border-muted hover:border-border'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: RING_CONFIG[blip.ring].color }}
                        />
                        <span className="font-medium text-foreground">{blip.name}</span>
                        {blip.isNew && <span className="text-xs bg-accent-muted text-accent px-1 rounded">NEW</span>}
                      </div>
                      <div className="text-xs text-foreground-tertiary mt-1 ml-5">
                        {QUADRANT_CONFIG[blip.quadrant].label} · {RING_CONFIG[blip.ring].label}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Ring summary */}
              <div className="mt-6 p-4 bg-cream/5 border border-border-muted rounded-lg">
                <h4 className="text-sm font-medium mb-3">Summary</h4>
                <div className="grid grid-cols-4 gap-2">
                  {(['adopt', 'trial', 'assess', 'hold'] as RadarRing[]).map((ring) => {
                    const count = currentRadar.blips.filter((b) => b.ring === ring).length;
                    return (
                      <div key={ring} className="text-center">
                        <div
                          className="text-lg font-bold"
                          style={{ color: RING_CONFIG[ring].color }}
                        >
                          {count}
                        </div>
                        <div className="text-xs text-foreground-tertiary">{RING_CONFIG[ring].label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Blip Editor Modal */}
          <BlipEditor
            blip={selectedBlip}
            isOpen={!!selectedBlip || isAddingBlip}
            onClose={() => {
              setSelectedBlip(null);
              setIsAddingBlip(false);
            }}
            onSave={handleSaveBlip}
            onDelete={handleDeleteBlip}
            isNew={isAddingBlip}
          />
        </div>
      </main>
    </>
  );
}
